import * as React from "react"
import axios from "axios"
import { useQueryClient } from "@tanstack/react-query"

import { api } from "@/api/instance"
import { useAuth } from "@/shared/lib/auth-context"
import {
  getProfileMeta,
  patchProfileMeta,
  setProfileMeta,
  useProfileMeta,
} from "@/entities/profile"
import { settled } from "@/shared/lib/settled"
import {
  isNonEmptyProfileData,
  normalizeProfileData,
} from "../components/ProfileView"
import { isTourProcessingHold } from "@/features/demo-tour"

/**
 * Loads profile detail when the selected profile needs hydration.
 * Refuses writes after unmount (and after rename removed the meta key).
 */
export function useProfileHydrator(profileId: string): void {
  const auth = useAuth()
  const queryClient = useQueryClient()
  const meta = useProfileMeta(profileId)
  const status = meta?.status
  const isInDatabase = meta?.isInDatabase

  // Unmount-only. The effect below patches the same meta it reads, so a guard
  // tied to effect cleanup would be cleared by that patch and would drop the
  // response of the request it had just started.
  const mounted = React.useRef(true)
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  React.useEffect(() => {
    const username = auth.user?.username
    if (!username || !profileId) return

    // Read from the cache: `meta` is a render snapshot and can lag behind a
    // load started by the task stream.
    const current = getProfileMeta(queryClient, profileId) ?? {}
    if (current.hasLoadedProfileData || current.isLoadingProfileData) return
    if (status === "not_started" && !isInDatabase) return
    // Tour hold: keep processing card; reveal loads detail later.
    if (isTourProcessingHold(profileId)) return

    const run = async () => {
      patchProfileMeta(queryClient, profileId, {
        isLoadingProfileData: true,
        errorMessage: null,
      })

      const result = await settled(api.v1.getProfile(profileId))
      if (!mounted.current) return
      // Rename moved the key away — do not resurrect a phantom under the old id.
      if (getProfileMeta(queryClient, profileId) === undefined) return

      if (!result.ok) {
        const err = result.error
        const httpStatus = axios.isAxiosError(err)
          ? err.response?.status
          : undefined
        const isServerError = httpStatus === undefined || httpStatus >= 500
        const cur = getProfileMeta(queryClient, profileId) ?? {}
        if (cur.status === "processing") {
          setProfileMeta(queryClient, profileId, {
            ...cur,
            hasLoadedProfileData: true,
            isLoadingProfileData: false,
          })
        } else {
          const preserveStatus =
            cur.status === "error" || cur.status === "success"
          setProfileMeta(queryClient, profileId, {
            ...cur,
            ...(isServerError
              ? {
                  status: "error",
                  errorMessage:
                    "Не удалось загрузить анкету профиля. Обновите страницу.",
                }
              : preserveStatus
                ? {}
                : { status: "not_started" }),
            profileData: undefined,
            hasLoadedProfileData: true,
            isLoadingProfileData: false,
          })
        }
        return
      }

      const presentatorData = result.value.data as unknown

      if (!isNonEmptyProfileData(presentatorData)) {
        const cur = getProfileMeta(queryClient, profileId) ?? {}
        const preserveStatus =
          cur.status === "processing" ||
          cur.status === "error" ||
          cur.status === "success"
        setProfileMeta(queryClient, profileId, {
          ...cur,
          ...(preserveStatus ? {} : { status: "not_started" }),
          profileData: undefined,
          hasLoadedProfileData: true,
          isLoadingProfileData: false,
        })
        return
      }

      const cur = getProfileMeta(queryClient, profileId) ?? {}
      setProfileMeta(queryClient, profileId, {
        ...cur,
        profileData: normalizeProfileData(presentatorData),
        hasLoadedProfileData: true,
        isLoadingProfileData: false,
      })
    }

    void run()
  }, [auth.user?.username, isInDatabase, profileId, queryClient, status])
}
